// How to play, from the Help button.
import { BREACH, DEFAULT_CONFIG, ENEMY_TYPES, NUCLEAR_SHIP_TYPES, NUCLEAR_TYPES, PRICE, REWARD, SHIP_TYPES, TILE, TOWERS } from '@thrash/sim';
import { openCredits } from './credits.ts';

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;');

function towerGuide(): string {
  return TOWERS.map(id => {
    const b = DEFAULT_CONFIG.buildings[id]!;
    const targets = (b.targets ?? ['infantry', 'vehicle']).join(', ');
    return `<li><strong>${esc(b.name)}</strong> $${PRICE[id]}${b.real ? ` <span class="muted">${esc(b.real)}</span>` : ''} · shoots ${esc(targets)}</li>`;
  }).join('');
}

function enemyGuide(): string {
  const road = ENEMY_TYPES.map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · pays $${REWARD[id]} · costs ${BREACH[id]} if it gets through</li>`;
  });
  const nuclear = NUCLEAR_TYPES.map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · DEFCON 1 only · pays $${REWARD[id]} · costs ${BREACH[id]} if it gets through, and levels ${Math.round((u.warhead?.radius ?? 0) / TILE)} tiles round where it lands</li>`;
  });
  const launches = (id: string): string => id === 'destroyer' ? 'Kalibr cruise missiles' : id === 'submarine' ? 'nuclear ICBMs (DEFCON 1 only)' : 'Shaheds';
  const ships = [...SHIP_TYPES, ...NUCLEAR_SHIP_TYPES].map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · offshore, out of reach · launches ${launches(id)} at the Command Center</li>`;
  });
  return [...road, ...nuclear, ...ships].join('');
}

export function openHelp(): void {
  if (document.getElementById('help')) return;
  const wrap = document.createElement('div');
  wrap.id = 'help';
  wrap.className = 'dialog';
  wrap.innerHTML = `<div class="box" role="dialog" aria-modal="true" aria-labelledby="help-title">
    <h2 id="help-title">How to play</h2>
    <p>The enemy comes down the road, wave after wave, and whatever reaches the end wears the Command Center
      down. Put towers beside the road to stop them. Each kill pays cash; so does every wave you hold. Anything that
      flies (drones, helicopters, cruise missiles) ignores the road: it comes in from any side of the map, over the
      sea too, and heads straight for the Command Center, shooting what it passes, so air defence has to cover every
      approach. At DEFCON 1 the enemy goes nuclear from the twentieth wave: a submarine offshore fires ICBMs and the
      glide vehicles carry nuclear warheads, and only THAAD can reach them.</p>
    <h3>Building</h3>
    <p>Pick a tower in the shop (or press its number), then click the ground beside the road. The ring shows how
      far it shoots. Mines go <em>on</em> the road. Click a tower to see what it does, change what it shoots first,
      upgrade it (three levels: more damage, more reach, more hit points) or sell it for 70 percent back.</p>
    <h3>Waves</h3>
    <p>Nothing comes until you press <strong>Start</strong> (Space), unless <strong>Auto</strong> (A) is on, which sends
      the next wave a few seconds after the last is held. Call the next wave while one is still on and you are paid
      extra for your nerve. The top bar shows what the next wave brings. Past wave twenty the enemy comes bigger and
      tougher every wave, for the record. Each tower shoots only what it is for: machine guns for men, anti-tank
      posts for armour, air defence for what flies, interceptors for missiles. Read the wave's line at the top
      before you press.</p>
    <h3>The enemy shoots back</h3>
    <p>Tanks shell your towers as they roll past, helicopters rake them, drones dive on them, marksmen pick off
      crews. A destroyed tower is rubble until you rebuild it. Their guns reach two to four tiles, so a tower five
      tiles back from the road is out of reach, and a Sniper Nest, an ATGM Post or a machine gun there still reaches
      the road. Mind the magazines: a Sniper Nest has 25 rounds a wave and an ATGM Post 12, while machine guns and
      bunkers never run dry. A Sniper Nest set to <em>Strongest</em> spends those rounds on anti-tank teams and
      gunners, the ones that wreck towers, not on conscripts. <strong>Artillery</strong> (Q) drops shells on a tile
      three seconds after the call, <strong>Smoke</strong> (W) blinds everything inside a cloud for eight seconds,
      and <strong>Emergency repair</strong> (E) puts 40 percent back into one tower, or the Command Center.</p>
    <h3>Patrols</h3>
    <p>A Tank Depot, a Helipad or a Carrier Air Wing fields one unit that roams round it and hunts what comes in reach;
      shot down, it is back in twenty seconds.</p>
    <h3>The fleet</h3>
    <p>From the sixth wave the enemy has ships out to sea, twenty tiles off the west coast, and nothing you build can
      reach them. They launch straight at the Command Center: drone carriers send Shaheds, frigates fire Kalibr cruise
      missiles, and each one that gets in costs integrity like anything off the road. The fight is in the air between
      the coast and the Command Center: Drone Interceptors and Air Defence for the Shaheds, an Iron Dome, a Patriot or
      a C-RAM for the Kalibrs, placed on the seaward side so they engage early. The ships sail out once their launchers
      are empty.</p>
    <h3>Saved games</h3>
    <p>The game is kept in this browser as you play; <strong>Continue</strong> on the menu picks it up where it was.
      Losing the Command Center ends it.</p>
    <h3>Camera</h3>
    <p>Drag to pan, wheel to zoom, the arrow keys to move. Hold Control and drag to turn the view; a Control-click
      puts it back.</p>
    <h3>The towers</h3>
    <ul class="units">${towerGuide()}</ul>
    <h3>The enemy</h3>
    <ul class="units">${enemyGuide()}</ul>
    <div class="row"><button class="credits link">Credits</button><button class="close primary">Close</button></div>
  </div>`;
  const close = () => wrap.remove();
  (wrap.querySelector('.close') as HTMLButtonElement).onclick = close;
  (wrap.querySelector('.credits') as HTMLButtonElement).onclick = () => openCredits();
  wrap.addEventListener('click', e => { if (e.target === wrap) close(); });
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') close(); e.stopPropagation(); });
  document.body.appendChild(wrap);
  (wrap.querySelector('.close') as HTMLButtonElement).focus();
}
